import {
	type CurrencyNetwork2,
	DateObject,
	Decimal,
	type StakeOperationStatusEnum,
	useFormatter
} from '@capsicum/common';

import { usePartners } from '../../partners/hooks/usePartners';
import { useFanTokenStakingSearchStakeOperationQuery } from '../queries/useFanTokenStakingSearchStakeOperationQuery';

type UseFanTokenStakingSearchStakeOperationProps = {
	currencyIds: Array<string>;
	statuses?: Array<StakeOperationStatusEnum>;
};

export type StakingOperationDetails = {
	currency: {
		currencyLogo?: {
			blurHash?: string;
			id?: string;
			url?: string;
		};
		decimalDetail?: number;
		id?: string;
		shortCode?: string;
		smartContractAddress?: string;
		symbol?: string;
	};
	currencyNetwork?: CurrencyNetwork2;
	hasCurrencyStakeOperation?: boolean;
	id?: string;
	nextRewardInDate?: DateObject;
	partner: {
		id?: string;
		name?: string;
	};
	smartContractAddress?: string;
	totalRewardPointsPool?: string;
	totalRewardPointsPoolFormatted?: string;
	totalStakedAmount?: string;
	totalStakedAmountFormatted?: string;
	user: {
		claimableAmount: string;
		claimableAmountFormatted?: string;
		claimableAmountRaw?: Decimal;
		claimableAndCooldownAmountFormatted?: string;
		cooldownAmountFormatted?: string;
		currentStake: string;
		currentStakeFormatted?: string;
		currentStakeRaw?: Decimal;
		hasClaimableAmount?: boolean;
		hasClaimableAndCooldownAmount?: boolean;
		hasCooldownAmount?: boolean;
		hasCurrentStake: boolean;
		hasLockedAmount?: boolean;
		hasStakableAmount?: boolean;
		hasUnstakableAmount?: boolean;
		latestEarnings?: string;
		lockedAmount?: string;
		stakableAmount: string;
		stakableAmountFormatted?: string;
		stakableAmountRaw?: Decimal;
		totalEarnings?: string;
		totalPendingCooldown?: string;
		unstakableAmount: string;
		unstakableAmountFormatted?: string;
		unstakableAmountRaw?: Decimal;
	};
};

export const useGetFanTokenStakeOperationByCurrencyIds = ({
	currencyIds,
	statuses = ['enabled']
}: UseFanTokenStakingSearchStakeOperationProps) => {
	const { getPartnerBy } = usePartners();
	const { formatCrypto } = useFormatter();
	const {
		data,
		isError: isOperationDetailsError,
		isLoading: isOperationDetailsLoading
	} = useFanTokenStakingSearchStakeOperationQuery({
		'currencyIds[]': currencyIds,
		'statuses[]': statuses
	});

	const stakeOperations = data?.map(res => {
		// Stake operation
		const hasCurrencyStakeOperation = Boolean(res?.id);

		const smartContractAddressFromNetwork = res?.currencyNetwork?.config?.smartContractAddress;
		const { blockchainSmartcontracts } = res?.currencyNetwork?.config || {};
		const { decimalDetail } = blockchainSmartcontracts?.[smartContractAddressFromNetwork || 0] || {};

		const totalRewardPointsPool = Number(
			res?.rewards?.find(r => r.type === 'reward_points_pool')?.totalRewardPoints || '0'
		).toFixed();

		const totalRewardPointsPoolFormatted = formatCrypto(totalRewardPointsPool, '');

		const totalStakedAmount = new Decimal(res?.amountStaked || '0').toFixed().toString();

		const totalStakedAmountFormatted = formatCrypto(totalStakedAmount, res?.currency?.shortCode || '');
		const nextRewardInDate = new DateObject(new DateObject().setUTCHours(24, 0, 0, 0));

		// Currency
		const currencyLogo = res?.currency?.images?.find(image => image.imageType.type === 'currency_logo');

		// Partner
		const operationPartner = getPartnerBy(p => p.currencyId === res?.currency?.id);

		// User Rewards
		const latestEarnings = formatCrypto(res.user?.latestEarnings || '0', '');
		const totalEarnings = formatCrypto(res.user?.totalEarnings || '0', '');

		// User stakable
		const stakableAmountRaw = new Decimal(res?.user?.stakableAmount || '0');
		const stakableAmount = new Decimal(res?.user?.stakableAmount || '0').toFixed().toString();
		const stakableAmountFormatted = formatCrypto(stakableAmount, res?.currency?.shortCode || '');
		const hasStakableAmount = stakableAmountRaw.greaterThan(0);

		// User staked
		const currentStakeRaw = new Decimal(res?.user?.currentStake || '0');
		const currentStake = currentStakeRaw.toFixed().toString();
		const currentStakeFormatted = formatCrypto(currentStake, res?.currency?.shortCode || '');
		const hasCurrentStake = currentStakeRaw.greaterThan(0);

		// User unstake
		const unstakableAmountRaw = new Decimal(res?.user?.unstakableAmount || '0');
		const unstakableAmount = unstakableAmountRaw.toString();
		const unstakableAmountFormatted = formatCrypto(unstakableAmount, res?.currency?.shortCode || '');
		const hasUnstakableAmount = unstakableAmountRaw.greaterThan(0);

		// User claim
		const claimableAmountRaw = new Decimal(res?.user?.claimableAmount || '0');
		const claimableAmount = claimableAmountRaw.toFixed().toString();
		const claimableAmountFormatted = formatCrypto(claimableAmount, res?.currency?.shortCode || '');
		const cooldownAmountRaw = new Decimal(res?.user?.totalPendingCooldown || '0');
		const cooldownAmount = cooldownAmountRaw.toFixed().toString();
		const cooldownAmountFormatted = formatCrypto(cooldownAmount, res?.currency?.shortCode || '');

		const claimableAndCooldownAmountRaw = claimableAmountRaw.add(cooldownAmount);
		const claimableAndCooldownAmount = claimableAndCooldownAmountRaw.toFixed().toString();

		const claimableAndCooldownAmountFormatted = formatCrypto(
			claimableAndCooldownAmount,
			res?.currency?.shortCode || ''
		);

		const hasClaimableAmount = claimableAmountRaw.greaterThan(0);
		const hasCooldownAmount = cooldownAmountRaw.greaterThan(0);
		const hasClaimableAndCooldownAmount = claimableAndCooldownAmountRaw.greaterThan(0);

		// User locked
		const lockedAmountRaw = currentStakeRaw.sub(unstakableAmount);
		const lockedAmount = lockedAmountRaw.toFixed().toString();
		const hasLockedAmount = lockedAmountRaw.greaterThan(0);

		// Prepare an object with the relevant details needed for staking flow
		return {
			...res,
			currency: {
				...res.currency,
				currencyLogo,
				decimalDetail
			},
			hasCurrencyStakeOperation,
			nextRewardInDate,
			partner: {
				id: operationPartner?.id,
				name: operationPartner?.name
			},
			totalRewardPointsPool,
			totalRewardPointsPoolFormatted,
			totalStakedAmount,
			totalStakedAmountFormatted,
			user: {
				...res.user,
				claimableAmount,
				claimableAmountFormatted,
				claimableAmountRaw,
				claimableAndCooldownAmount,
				claimableAndCooldownAmountFormatted,
				cooldownAmount,
				cooldownAmountFormatted,
				currentStake,
				currentStakeFormatted,
				hasClaimableAmount,
				hasClaimableAndCooldownAmount,
				hasCooldownAmount,
				hasCurrentStake,
				hasLockedAmount,
				hasStakableAmount,
				hasUnstakableAmount,
				latestEarnings,
				lockedAmount,
				stakableAmount,
				stakableAmountFormatted,
				stakableAmountRaw,
				totalEarnings,
				unstakableAmount,
				unstakableAmountFormatted,
				unstakableAmountRaw
			}
		};
	});

	const getOperationDetails = (currencyId?: string): StakingOperationDetails | undefined =>
		stakeOperations?.find(operation => operation?.currencyId === currencyId);

	return {
		getOperationDetails,
		isOperationDetailsError,
		isOperationDetailsLoading,
		stakeOperations
	};
};
